import React, {
  useEffect,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  Avatar,
  Button,
  Input,
  List,
  Popconfirm,
  Select,
} from 'antd';

import i18n from '../../config/i18n';
import ImageUploader from '../shared/ImageUploader';
import MemberListItem from './MemberListItem';

import defaultUserAvatarImage from '../../styles/images/no_testimonial_avatar_image.png';
import { PATIENT_ID } from '../../constants/RoleConstants';

const styles = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  uploadAvatar: {
    borderBottom: 'solid 1px gray',
  },
  editName: {
    minHeight: '104px',
  },
  addListWrapper: {
    height: 'calc(100% - 101px)',
  },
  listWrapper: {
    height: 'calc(100% - 48px)',
  },
  memberList: {
    height: 'calc(100% - 153px)',
  },
  select: {
    width: '100%',
  },
  list: {
    height: '100%',
    overflowY: 'scroll',
  },
  button: {
    normal: {
      border: 'none',
      marginLeft: '6px',
    },
    close: {
      border: 'none',
      backgroundColor: 'none',
    },
  },
  title: {
    fontSize: '15px',
    padding: '12px',
    backgroundColor: '#2D3E4F',
    color: '#FFFFFF',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  content: {
    margin: '12px',
    width: 'calc(100% - 24px)',
  },
  user: {
    item: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'start',
    },
    avatar: {
      marginRight: '9px',
      minWidth: '32px',
    },
    name: {
      marginRight: '9px',

      // 50px is the sum of avatar.marginRight, avatar.minWidth and name.marginRight
      maxWidth: 'calc(100% - 50px)',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    withCloseButton: {
      // 74px is the sum of avatar.marginRight, avatar.minWidth,
      // name.marginRight and the close button width
      maxWidth: 'calc(100% - 74px)',
    },
  },
};

const ChannelSetting = ({
  actions,
  channelId,
  channelName,
  channelMembers,
  currentUserId,
  creatorId,
  invitableUsers,
  onModifyTitle,
  onAddMembers,
  onUploadAvatar,
}) => {
  const [newChannelName, setNewChannelName] = useState(channelName);
  const [memberItems, setMemberItems] = useState([]);
  const [isAdding, setIsAdding] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [isPatientSelected, setIsPatientSelected] = useState(false);
  const [hasPatientMember, setHasPatientMember] = useState(false);
  const [deletingMembers, setDeletingMembers] = useState({});

  const handleDeselect = (userId, roleId) => {
    setSelectedUserIds(selectedUserIds.filter(id => id !== userId));
    if (roleId === PATIENT_ID) setIsPatientSelected(false);
  };

  const generateSelectedUserItems = (userId) => {
    const {
      avatar,
      firstName,
      lastName,
      roleId,
    } = invitableUsers.find(user => user.id === userId);
    return (
      <List.Item style={styles.user.item}>
        <Button
          icon="close"
          onClick={() => handleDeselect(userId, roleId)}
          size="small"
          style={styles.close}
          type="link"
        />
        <Avatar
          src={defaultUserAvatarImage}
          srcSet={avatar}
          style={styles.user.avatar}
        />
        <div style={{ ...styles.user.name, ...styles.user.withCloseButton }}>
          {firstName} {lastName}
        </div>
      </List.Item>
    );
  };

  const generateSelectOptions = users => (
    users
      .filter(({ id }) => !selectedUserIds.includes(id))
      .map(({
        avatar,
        id,
        firstName,
        lastName,
        roleId,
      }) => {
        const fullName = `${firstName} ${lastName}`;
        return (
          <Select.Option
            disabled={(hasPatientMember || isPatientSelected) && roleId === PATIENT_ID}
            key={id}
            label={fullName}
            value={id}
            style={styles.user.item}
            role={roleId}
          >
            <Avatar
              src={defaultUserAvatarImage}
              srcSet={avatar}
              style={styles.user.avatar}
            />
            <div style={styles.user.name}>{fullName}</div>
          </Select.Option>
        );
      })
  );

  const handleDeleteMember = async (memberId) => {
    setDeletingMembers({ ...deletingMembers, [memberId]: true });
    await actions.deleteChannelMemberById(channelId, memberId);
    setDeletingMembers({ ...deletingMembers, [memberId]: false });
  };

  const generateMemberItem = data => (
    data.map(({ id, user }) => (
      <MemberListItem
        actions={[
          {
            key: 'delete',
            children: (
              <Popconfirm
                title={i18n.t(currentUserId === user.id ? 'sureToLeave' : 'sureToDelete')}
                onConfirm={() => handleDeleteMember(id)}
                cancelText={i18n.t('cancel')}
                okText={i18n.t('confirm')}
              >
                {i18n.t(currentUserId === user.id ? 'leave' : 'delete')}
              </Popconfirm>
            ),
          },
        ]}
        loading={deletingMembers[id]}
        showMenu={currentUserId === creatorId || currentUserId === user.id}
        user={user}
      />
    ))
  );

  useEffect(() => {
    setMemberItems(generateMemberItem(channelMembers));
    setHasPatientMember(channelMembers.some(member => member.user.roleId === PATIENT_ID));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelMembers, deletingMembers]);

  useEffect(() => {
    setNewChannelName(channelName);
  }, [channelName]);

  // Edit name.
  const handleEditName = () => {
    const trimName = newChannelName.trim();
    if (trimName !== channelName) {
      onModifyTitle(trimName);
    }
  };
  const handleInput = (e) => {
    setNewChannelName(e.target.value);
  };
  const handleFocus = () => {
    if (newChannelName.trim() === i18n.t('newChannel')) {
      setNewChannelName('');
    }
  };

  // Add members.
  const handleStartAdd = () => {
    setIsAdding(true);
  };
  const handleCancel = () => {
    setIsAdding(false);
    setSelectedUserIds([]);
    setIsPatientSelected(false);
  };
  const handleConfirm = () => {
    onAddMembers(selectedUserIds);
    setIsAdding(false);
    setSelectedUserIds([]);
  };
  const handleSelect = (value, option) => {
    setSelectedUserIds([...selectedUserIds, value]);
    if (option.props.role === PATIENT_ID) setIsPatientSelected(true);
  };

  return (
    <div
      style={styles.root}
    >
      <div style={styles.uploadAvatar}>
        <div style={styles.title}>
          {i18n.t('upload')} {i18n.t('avatar')}
          <ImageUploader
            actions={actions}
            doCrop
            hint={i18n.t('avatarHint')}
            title={`${i18n.t('upload')} ${i18n.t('avatar')}`}
            onUpload={onUploadAvatar}
          >
            <Button
              size="small"
              style={styles.button.normal}
            >
              {i18n.t('selectFile')}
            </Button>
          </ImageUploader>
        </div>
      </div>
      <div style={styles.editName}>
        <div style={styles.title}>
          {i18n.t('edit')} {i18n.t('name')}
          <Button
            disabled={channelName === newChannelName}
            onClick={handleEditName}
            size="small"
            style={styles.button.normal}
          >
            {i18n.t('save')}
          </Button>
        </div>
        <div style={styles.content}>
          <Input
            value={newChannelName}
            onChange={handleInput}
            onFocus={handleFocus}
          />
        </div>
      </div>
      {
        isAdding ? (
          <div style={styles.memberList}>
            <div style={styles.title}>
              {i18n.t('add')} {selectedUserIds.length} {i18n.t('members')}
              <div>
                <Button
                  onClick={handleCancel}
                  size="small"
                  style={styles.button.normal}
                >
                  {i18n.t('cancel')}
                </Button>
                <Button
                  disabled={selectedUserIds.length <= 0}
                  onClick={handleConfirm}
                  size="small"
                  style={styles.button.normal}
                >
                  {i18n.t('confirm')}
                </Button>
              </div>
            </div>
            <div style={{ ...styles.content, ...styles.addListWrapper }}>
              <Select
                onSelect={handleSelect}
                optionFilterProp="label"
                placeholder={i18n.t('addMembersHint')}
                showSearch
                style={styles.select}
                value={undefined}
              >
                {generateSelectOptions(invitableUsers)}
              </Select>
              <List
                itemLayout="vertical"
                dataSource={selectedUserIds}
                renderItem={generateSelectedUserItems}
                size="small"
                style={styles.list}
              />
            </div>
          </div>
        ) : (
          <div style={styles.memberList}>
            <div style={styles.title}>
              {channelMembers.length} {i18n.t('members')}
              <Button
                onClick={handleStartAdd}
                size="small"
                style={styles.button.normal}
              >
                {i18n.t('add')}
              </Button>
            </div>
            <div style={{ ...styles.listWrapper }}>
              <List
                dataSource={memberItems}
                renderItem={item => item}
                style={styles.list}
              />
            </div>
          </div>
        )
      }
    </div>
  );
};

ChannelSetting.propTypes = {
  actions: PropTypes.object.isRequired,
  channelId: PropTypes.number.isRequired,
  channelName: PropTypes.string,
  channelMembers: PropTypes.arrayOf(PropTypes.object),
  currentUserId: PropTypes.number.isRequired,
  creatorId: PropTypes.number.isRequired,
  invitableUsers: PropTypes.arrayOf(PropTypes.object),
  onModifyTitle: PropTypes.func.isRequired,
  onAddMembers: PropTypes.func.isRequired,
  onUploadAvatar: PropTypes.func.isRequired,
};

ChannelSetting.defaultProps = {
  channelName: '',
  channelMembers: [],
  invitableUsers: [],
};

export default ChannelSetting;



// WEBPACK FOOTER //
// ./src/components/messaging/ChannelSetting.js